import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Title, Legend, Tooltip } from 'chart.js';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Legend,
  Tooltip
);

const ChiSquareTestCalculator = () => {
  const [observed, setObserved] = useState('');
  const [expected, setExpected] = useState('');
  const [alpha, setAlpha] = useState('0.05');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const calculateChiSquareTest = () => {
    try {
      const observedArray = observed.split(',').map(Number);
      const expectedArray = expected.split(',').map(Number);
      const alphaValue = parseFloat(alpha);

      if (observedArray.length !== expectedArray.length) {
        setError('The number of observed and expected values must be the same');
        return;
      }

      if (isNaN(alphaValue) || alphaValue <= 0 || alphaValue >= 1) {
        setError('Please enter a valid alpha value between 0 and 1');
        return;
      }

      if (observedArray.some(isNaN) || expectedArray.some(isNaN)) {
        setError('Please enter valid numbers for observed and expected values');
        return;
      }

      // Calculate chi-square statistic
      const chiSquare = observedArray.reduce((sum, obs, index) => {
        const exp = expectedArray[index];
        return sum + Math.pow(obs - exp, 2) / exp;
      }, 0);

      // Calculate degrees of freedom
      const df = observedArray.length - 1;

      // Calculate p-value (simplified)
      const pValue = 1 - Math.exp(-chiSquare / 2);

      // Create chart data
      const chartData = {
        labels: Array.from({ length: observedArray.length }, (_, i) => `Category ${i + 1}`),
        datasets: [
          {
            label: 'Observed Frequencies',
            data: observedArray,
            backgroundColor: '#2563eb',
            borderColor: '#2563eb',
            borderWidth: 1,
          },
          {
            label: 'Expected Frequencies',
            data: expectedArray,
            backgroundColor: '#10b981',
            borderColor: '#10b981',
            borderWidth: 1,
          }
        ]
      };

      setResult({
        chiSquare: chiSquare.toFixed(4),
        pValue: pValue.toFixed(4),
        df: df,
        alpha: alphaValue,
        rejectNull: pValue < alphaValue
      });

      setChartData(chartData);
      setError('');
    } catch (err) {
      setError('An error occurred while calculating the test. Please check your inputs.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Chi-Square Test Calculator - Hypothesis Testing</title>
        <meta name="description" content="Calculate Chi-Square Test statistics for testing independence of categorical variables. Use our interactive calculator to analyze your data." />
        <meta name="keywords" content="chi-square test calculator, hypothesis testing, statistics calculator, independence test" />
        <meta property="og:title" content="Chi-Square Test Calculator - Hypothesis Testing" />
        <meta property="og:description" content="Calculate Chi-Square Test statistics for testing independence of categorical variables. Use our interactive calculator to analyze your data." />
        <meta name="twitter:title" content="Chi-Square Test Calculator - Hypothesis Testing" />
        <meta name="twitter:description" content="Calculate Chi-Square Test statistics for testing independence of categorical variables. Use our interactive calculator to analyze your data." />
      </Helmet>

      <div className="statistics-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">🧬</div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Chi-Square Test Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Hypothesis Testing</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            Calculate Chi-Square Test statistics for testing independence of categorical variables. This calculator helps you analyze whether there is a significant association between two categorical variables.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
        >
          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="observed">
              Observed Frequencies (comma-separated values):
            </label>
            <textarea
              id="observed"
              name="observed"
              value={observed}
              onChange={(e) => setObserved(e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter observed frequencies (e.g., 10,20,30,40)"
              rows="3"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="expected">
              Expected Frequencies (comma-separated values):
            </label>
            <textarea
              id="expected"
              name="expected"
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter expected frequencies (e.g., 15,15,15,15)"
              rows="3"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="alpha">
              Significance Level (α):
            </label>
            <input
              type="text"
              id="alpha"
              name="alpha"
              value={alpha}
              onChange={(e) => setAlpha(e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter significance level (e.g., 0.05)"
            />
          </div>

          <button
            onClick={calculateChiSquareTest}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
          >
            Calculate Chi-Square Test
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}

          {chartData && (
            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4">Data Visualization</h3>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <Bar
                  data={chartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: true,
                        text: 'Observed vs Expected Frequencies'
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          {result && (
            <div className="results-section mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Chi-Square Test Results</h4>
                  <div className="space-y-2">
                    <p className="flex justify-between">
                      <span>Chi-Square Value</span>
                      <span className="font-medium">{result.chiSquare}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>P-value</span>
                      <span className="font-medium">{result.pValue}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Degrees of Freedom</span>
                      <span className="font-medium">{result.df}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Significance Level (α)</span>
                      <span className="font-medium">{result.alpha}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Reject Null Hypothesis</span>
                      <span className={`font-medium ${result.rejectNull ? 'text-red-500' : 'text-green-500'}`}>
                        {result.rejectNull ? 'Yes' : 'No'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Let's analyze the relationship between gender and preference for a new product:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Data:</h4>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h5 className="font-semibold mb-2">Observed Frequencies:</h5>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Male: 120</li>
                  <li>Female: 80</li>
                  <li>Male: 70</li>
                  <li>Female: 130</li>
                </ul>
              </div>
              <div>
                <h5 className="font-semibold mb-2">Expected Frequencies (if independent):</h5>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Male: 95</li>
                  <li>Female: 105</li>
                  <li>Male: 95</li>
                  <li>Female: 105</li>
                </ul>
              </div>
            </div>
            
            <h4 className="font-semibold mt-4 mb-2">Results:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Chi-Square Value: 20.4082</li>
              <li>P-value: 0.00001</li>
              <li>Degrees of Freedom: 3</li>
              <li>Reject Null Hypothesis: Yes</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Chi-Square Test Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Prepare Your Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Collect categorical data for both variables</li>
                <li>Count the frequencies for each category combination</li>
                <li>Calculate expected frequencies if the variables were independent</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Paste or type observed frequencies in the first input field</li>
                <li>Paste or type expected frequencies in the second input field</li>
                <li>Set your desired significance level (α)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click "Calculate Chi-Square Test" to get results</li>
                <li>Review the Chi-Square value, P-value, and degrees of freedom</li>
                <li>Check if the null hypothesis should be rejected</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-6">
            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What is a Chi-Square Test?</h3>
              <p className="text-gray-600">
                A Chi-Square Test is a statistical test used to determine if there is a significant association between two categorical variables.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">When should I use a Chi-Square Test?</h3>
              <p className="text-gray-600">
                Use a Chi-Square Test when you want to analyze the relationship between two categorical variables (e.g., gender vs. product preference, education level vs. employment status).
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What are the assumptions of Chi-Square Test?</h3>
              <p className="text-gray-600">
                The main assumptions are:
                <ul className="list-disc pl-8 space-y-1">
                  <li>Data must be categorical</li>
                  <li>Observations must be independent</li>
                  <li>Expected frequencies should be at least 5 in most cells</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What does the P-value mean?</h3>
              <p className="text-gray-600">
                The P-value indicates the probability of observing the data if the null hypothesis (no association) is true. A P-value less than your significance level (α) suggests rejecting the null hypothesis.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold mb-2">Statistical Analysis</h3>
              <p className="text-gray-600">Calculate Chi-Square values and P-values for categorical data analysis</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-lg font-semibold mb-2">Easy Input</h3>
              <p className="text-gray-600">Simple interface for entering observed and expected frequencies</p>
            </div>
            <div className="text-center p-6 bg-yellow-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Instant Results</h3>
              <p className="text-gray-600">Get immediate calculations and statistical significance</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-lg font-semibold mb-2">Educational Value</h3>
              <p className="text-gray-600">Learn about Chi-Square Tests and categorical data analysis</p>
            </div>
            <div className="text-center p-6 bg-pink-50 rounded-lg">
              <div className="text-4xl mb-4">⚙️</div>
              <h3 className="text-lg font-semibold mb-2">Customizable</h3>
              <p className="text-gray-600">Adjust significance level for different requirements</p>
            </div>
            <div className="text-center p-6 bg-orange-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Multiple Uses</h3>
              <p className="text-gray-600">Perfect for market research, social sciences, and quality control</p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Chi-Square Tests <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Chi-Square Tests are statistical tests used to analyze categorical data. They help determine if there is a significant association between two categorical variables. The test compares observed frequencies with expected frequencies under the assumption of independence.
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Testing Independence:</span> Checking if two categorical variables are related
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Goodness of Fit:</span> Testing if observed data fits a theoretical distribution
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Contingency Tables:</span> Analyzing relationships in cross-tabulated data
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Research Applications:</span> Widely used in social sciences, market research, and quality control
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Ensure your data meets the assumptions of the Chi-Square Test (independent observations, categorical data, minimum expected frequencies) before using this calculator.
          </p>
        </div>
      </div>
    </>
  );
};

export default ChiSquareTestCalculator;
